/**
 * Client-side realistic telecom/CRM test data generator.
 * Generates data matching the Authmatech Connect import schema.
 */

const FIRST_NAMES = [
  'Ahmed','Sara','Mohammad','Fatima','Khalid','Layla','Omar','Nour','Rami','Hana',
  'Abdullah','Mona','Tariq','Rania','Faisal','Dina','Sami','Amal','Walid','Maya',
  'Yousef','Rana','Adel','Salma','Hassan','Nadia','Ziad','Lina','Samir','Noura',
  'Ibrahim','Heba','Karim','Wafa','Jad','Lama','Mazen','Ghada','Tarek','Sana',
  'Bilal','Mais','Raed','Leen','Nabil','Reem','Hamza','Nihal','Amr','Shirin',
];

const LAST_NAMES = [
  'Al-Rashidi','Khalil','Hassan','Al-Zahrawi','Nasser','Al-Mansouri','Karimi','Othman',
  'Al-Ahmad','Haddad','Saleh','Barakat','Qasim','Al-Farsi','Nazzal','Al-Khatib',
  'Hamdan','Mustafa','Al-Omari','Ibrahim','Khoury','Saeed','Al-Harbi','Younis',
  'Al-Mutairi','Taha','Al-Balushi','Nassar','Shabaan','Al-Shamsi','Rizk','Al-Dosari',
];

const EMAIL_DOMAINS = ['gmail.com','yahoo.com','outlook.com','hotmail.com','icloud.com'];

const COUNTRIES: { code: string; prefix: string; operators: string[] }[] = [
  { code: 'JO', prefix: '+962', operators: ['Zain JO','Orange JO','Umniah'] },
  { code: 'SA', prefix: '+966', operators: ['STC','Mobily','Zain SA','Virgin SA'] },
  { code: 'AE', prefix: '+971', operators: ['du','Etisalat (e&)'] },
  { code: 'KW', prefix: '+965', operators: ['Zain KW','Ooredoo KW','STC KW'] },
  { code: 'QA', prefix: '+974', operators: ['Ooredoo','Vodafone QA'] },
  { code: 'BH', prefix: '+973', operators: ['Batelco','Zain BH','STC BH'] },
  { code: 'OM', prefix: '+968', operators: ['Omantel','Ooredoo OM'] },
];

const VERIFICATION_STATUSES = ['MATCH','MATCH','MATCH','MATCH','NO_MATCH'] as const;
const FRAUD_RISKS = ['LOW','LOW','LOW','LOW','MEDIUM','MEDIUM','HIGH'] as const;

function rand(arr: readonly string[]): string {
  return arr[Math.floor(Math.random() * arr.length)] ?? arr[0]!;
}

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randomMobile(prefix: string): string {
  const n = randInt(70000000, 99999999);
  return `${prefix}${n}`;
}

function randomDate(): string {
  const now = Date.now();
  const sixMonthsAgo = now - 180 * 24 * 60 * 60 * 1000;
  const ts = sixMonthsAgo + Math.random() * (now - sixMonthsAgo);
  return new Date(ts).toISOString().split('T')[0] ?? '';
}

export interface ImportRow {
  externalId:          string;
  firstName:           string;
  lastName:            string;
  email:               string;
  mobileNumber:        string;
  country:             string;
  operator:            string;
  verificationStatus:  string;
  trustScore:          number;
  fraudRisk:           string;
  createdAt:           string;
}

export const IMPORT_HEADERS: (keyof ImportRow)[] = [
  'externalId','firstName','lastName','email','mobileNumber',
  'country','operator','verificationStatus','trustScore','fraudRisk','createdAt',
];

export function generateRows(count: number): ImportRow[] {
  const rows: ImportRow[] = [];
  for (let i = 0; i < count; i++) {
    const fn   = rand(FIRST_NAMES);
    const ln   = rand(LAST_NAMES);
    const ctry = COUNTRIES[randInt(0, COUNTRIES.length - 1)] ?? COUNTRIES[0]!;
    const vstatus = rand(VERIFICATION_STATUSES);
    const frisk   = rand(FRAUD_RISKS);
    const trust   = vstatus === 'MATCH'
      ? randInt(65, 98)
      : randInt(10, 50);

    rows.push({
      externalId:         `CRM-${String(i + 1).padStart(7, '0')}`,
      firstName:           fn,
      lastName:            ln,
      email:               `${fn.toLowerCase().replace(/[^a-z]/g, '')}.${ln.toLowerCase().replace(/[^a-z]/g, '')}.${randInt(1, 99)}@${rand(EMAIL_DOMAINS)}`,
      mobileNumber:        randomMobile(ctry.prefix),
      country:             ctry.code,
      operator:            rand(ctry.operators),
      verificationStatus:  vstatus,
      trustScore:          trust,
      fraudRisk:           frisk,
      createdAt:           randomDate(),
    });
  }
  return rows;
}

/** Build a CSV string from rows */
export function rowsToCsv(rows: ImportRow[]): string {
  const header = IMPORT_HEADERS.join(',');
  const lines = rows.map(r =>
    IMPORT_HEADERS.map(h => {
      const v = String(r[h]);
      return v.includes(',') ? `"${v}"` : v;
    }).join(',')
  );
  return [header, ...lines].join('\n');
}

/** Trigger a browser download of the given content */
export function downloadBlob(content: string, filename: string, mime = 'text/csv'): void {
  const blob = new Blob([content], { type: mime });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Generate and download an XLSX file using SheetJS */
export async function downloadXlsx(rows: ImportRow[], filename: string): Promise<void> {
  const XLSX = await import('xlsx');
  const data = [IMPORT_HEADERS, ...rows.map(r => IMPORT_HEADERS.map(h => r[h]))];
  const ws   = XLSX.utils.aoa_to_sheet(data);
  const wb   = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Import Data');
  XLSX.writeFile(wb, filename);
}
